function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import produce from 'immer';
import moment from 'moment';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { h } from 'panel';
import _shuffle from 'lodash/shuffle';
import type { Location } from 'react-router';
import { CardJsxProps, CardReact } from 'apps/boards/src/dashboards-app/card';
import { MIN_CARD_HEIGHT } from 'apps/boards/src/dashboards-app/card/constants';
import { getDashboardCardParams } from 'apps/boards/src/dashboards-app/util';
import { AppMode } from 'apps/boards/src/models/enums';
import { getBookmarkChartOptions } from 'apps/boards/src/report-util';
import { createCardResults, queryCanonicalBookmark } from 'apps/boards/src/report-util/query';
import { generateSparkReport, SparkGenerationError, SparkGenerationErrorType, SparkUsageLimitHitError } from 'common/ai';
import { AIQuery } from 'common/ai/types';
import { Visibility, VisibilityChangeEvent } from 'common/components/base/stackable';
import { ButtonJsxProps, ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { DropMenuReact } from 'common/components/drop-menu';
import { Directions, PositioningStrategy } from 'common/components/drop-menu/types';
import { InputReact } from 'common/components/input';
import { InputSize } from 'common/components/input/types';
import { ItemsMenuReact } from 'common/components/items-menu';
import { Section } from 'common/components/items-menu/types';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize, SpinnerTheme } from 'common/components/spinner/types';
import { SvgIconReact } from 'common/components/svg-icon';
import { IronContextRegistry } from 'common/report/context-registry';
import { ContentType, Dashboard, DashboardCard, DashboardQueryOrigin, DisplayBookmark } from 'common/report/dashboards/types';
import { reporter } from 'common/report/error-reporting';
import { getReportUrl, getWorkspaceId } from 'common/report/util';
import { KEY_VALUES } from 'common/util/dom';
import { generateShortcutLabel, getCommandKey, keyEventSwitcher } from 'common/util/keyboard';
import { getNow } from 'common/util/timing';
import { promptFeedbackModal } from 'common/widgets/feedback-modal';
import { FeedbackForFeature, Sentiment } from 'common/widgets/feedback-modal/types';
import { react2panel } from 'common/util/react2panel';
import { SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightReact } from 'common/components/text-highlight';
import { trackHotjarEvent } from 'common/util/hotjar';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { LocalizationStore } from 'common/i18n/store';
import { CurrentProject } from 'common/report/model-resources/projects';
import { useTranslation } from 'common/i18n/hooks';
import { getUpsellInfo, ModalTrigger } from 'common/widgets/in-product-upsell-modal/util';
import { promptInProductUpsellModal } from 'common/widgets/in-product-upsell-modal/prompt';
import { Trans, TranslationKey } from 'common/i18n';
import { useGlobalSidenavController } from 'common/contexts/react/global-sidenav-controller-context';
import { renderCardTitleActions } from './card-title';
import styles from './index.module.styl';
const MIN_HEIGHT = MIN_CARD_HEIGHT[ContentType.Spark];
type AICard = {
  /** The prompt used to generate this card. */
  prompt: string;
  /** The previous AI response used to help generate this card. */
  reportContext?: AIQuery;
  /** The raw AI response. */
  aiQuery: AIQuery;
  /** The AI response transformed into a bookmark. */
  bookmarkQuery: DisplayBookmark;
  /** Props passed to CardEl to preview the report.  */
  params: CardJsxProps[`params`];
  /** User-submitted sentiment (positive/negative). */
  sentiment?: Sentiment;
  /** The time the query was made. */
  time: moment.Moment;
};
type SparkCardState = {
  cards: AICard[];
  currentCardIdx: number;
  isLoading: boolean;
  historyOpen: boolean;
  promptInput: string;
  reportGenerationError: SparkGenerationErrorType;
  usage: number | null;
  usageLimit: number | null;
};
export type SparkCardProps = {
  dashboard: Dashboard;
  getReportUrl: (bookmark: DisplayBookmark) => string;
  onEditReport: (bookmark: DisplayBookmark) => void;
  onSaveReport: (bookmark: DisplayBookmark) => void;
  onRemove?: () => void;
  onMinHeightChanged?: (minHeight: number) => void;
  location: Location;
};
const PROMPT_EXAMPLES: TranslationKey[] = ["spark.prompt-sample-1", "spark.prompt-sample-2", "spark.prompt-sample-3", "spark.prompt-sample-4", "spark.prompt-sample-5", "spark.prompt-sample-6", "spark.prompt-sample-7", "spark.prompt-sample-8", "spark.prompt-sample-9"];
class SparkCard extends React.PureComponent<Pick<IronContextRegistry, `currentProject` | `metrics` | `behaviors` | `i18n` | `globalSidenavController`> & SparkCardProps, SparkCardState> {
  constructor() {
    super(...arguments);
    _defineProperty(this, "sessionId", nanoid());
    _defineProperty(this, "abortController", null);
    _defineProperty(this, "el", React.createRef<HTMLDivElement>());
    _defineProperty(this, "samplePrompts", _shuffle(PROMPT_EXAMPLES));
    _defineProperty(this, "state", {
      currentCardIdx: -1,
      cards: [],
      isLoading: false,
      promptInput: "",
      reportGenerationError: null,
      historyOpen: false,
      usage: null,
      usageLimit: null
    });
    _defineProperty(this, "onKeyPress", (ev: KeyboardEvent) => {
      keyEventSwitcher({
        ["".concat(getCommandKey(), " + ").concat(KEY_VALUES.ENTER)]: () => this.saveCard(),
        [KEY_VALUES.ENTER]: () => this.submitPrompt(),
        [KEY_VALUES.ESCAPE]: () => this.close()
      }, {
        stopPropagation: true,
        usableInInputs: true
      })(ev);
    });
  }
  componentDidMount() {
    var _this$props$onMinHeig, _this$props;
    // imitating card behavior to set the row height
    (_this$props$onMinHeig = (_this$props = this.props).onMinHeightChanged) === null || _this$props$onMinHeig === void 0 || _this$props$onMinHeig.call(_this$props, MIN_HEIGHT);

    // track add
    this.trackEvent("[AI] Spark Launched", {});
    trackHotjarEvent("[AI] Spark Launched");
    this.el.current.addEventListener("keydown", this.onKeyPress);
  }
  componentWillUnmount() {
    this.el.current.removeEventListener("keydown", this.onKeyPress);
  }

  /**
   * Gets the currently displayed card.
   */
  getCard(): AICard | null {
    return this.state.cards[this.state.currentCardIdx] || null;
  }
  getHistorySections(): Section[] {
    const {
      t
    } = this.props.i18n;
    return [{
      items: this.state.cards.map((card, idx) => ({
        id: idx.toString(),
        label: card.prompt,
        subLabel: this.props.i18n.timeFromNow(card.time),
        subLabelBadge: idx === this.state.currentCardIdx ? t("spark.card-displayed") : "",
        wrapLabel: true,
        wrapSubLabel: true
      })).reverse()
    }];
  }
  getFeedbackButtonProps(sentiment: Sentiment): ButtonJsxProps {
    const {
      t
    } = this.props.i18n;
    const card = this.getCard();
    const isPositive = sentiment === Sentiment.Positive;
    return {
      attrs: {
        disabled: !!(card !== null && card !== void 0 && card.sentiment),
        icon: isPositive ? "thumbs-up" : "thumbs-down",
        pressed: (card === null || card === void 0 ? void 0 : card.sentiment) === sentiment,
        size: ButtonSize.Small,
        theme: ButtonTheme.Tertiary,
        elref: "".concat(sentiment, "FeedbackButton"),
        tooltip: {
          text: isPositive ? t("spark.feedback-tooltip-positive") : t("spark.feedback-tooltip-negative")
        }
      },
      on: {
        click: async () => {
          const selectedCard = this.getCard();
          const [, finalSentiment] = await promptFeedbackModal({
            feature: FeedbackForFeature.Spark,
            'negative-label': "",
            placeholder: "Additional feedback (Optional)",
            'sentiment-heading': "Rate your satisfaction with this response",
            'positive-label': "",
            title: "Additional Spark Feedback"
          }, {
            extraFeedbackEventName: "[AI] Spark Feedback",
            extraFeedbackEventProperties: {
              prompt: selectedCard.prompt,
              reportContext: selectedCard === null || selectedCard === void 0 ? void 0 : selectedCard.aiQuery,
              aiQuery: selectedCard.aiQuery,
              bookmark: selectedCard.bookmarkQuery
            },
            initialSentiment: sentiment
          });
          this.setState({
            cards: produce(this.state.cards, draft => {
              const c = draft[this.state.currentCardIdx];
              c.sentiment = finalSentiment;
            })
          });
        }
      }
    };
  }
  close() {
    var _this$abortController, _this$props$onRemove, _this$props2;
    (_this$abortController = this.abortController) === null || _this$abortController === void 0 || _this$abortController.abort();
    (_this$props$onRemove = (_this$props2 = this.props).onRemove) === null || _this$props$onRemove === void 0 || _this$props$onRemove.call(_this$props2);
  }
  saveCard() {
    const card = this.getCard();
    if (card) {
      this.props.onSaveReport(card.bookmarkQuery);
      this.trackEvent("[AI] Spark Report Saved", {
        prompts: this.state.cards.map(c => c.prompt),
        aiQuery: card.aiQuery
      });
    }
  }
  onClickContactSupport(ev: React.MouseEvent) {
    ev.preventDefault();
    this.props.globalSidenavController.openContactSupport();
  }
  onClickClearError() {
    this.setState({
      reportGenerationError: null,
      currentCardIdx: this.state.cards.length - 1,
      promptInput: ""
    });
  }
  async submitPrompt(prompt?: string) {
    const currentProject = this.getCurrentProject();
    prompt = (prompt || this.state.promptInput).trim();
    if (this.state.isLoading || !prompt || !currentProject) {
      return;
    }
    this.abortController = new AbortController();
    this.setState({
      isLoading: true,
      promptInput: prompt,
      reportGenerationError: null
    });
    const currentCard = this.getCard();
    let aiQuery, bookmarkQuery;
    const generationStartTime = getNow();
    let aiResponseTime, queryApiResponseTime;
    const reportContext = currentCard === null || currentCard === void 0 ? void 0 : currentCard.aiQuery;
    try {
      var _this$props$getReport, _this$props3;
      const workspaceId = parseInt(getWorkspaceId(), 10);
      const response = await generateSparkReport(currentProject.id, workspaceId, prompt, {
        abortSignal: this.abortController.signal
      }, reportContext);
      aiResponseTime = getNow();
      aiQuery = response.aiQuery;
      bookmarkQuery = response.bookmark;

      // dummy card to use within the CardEl component
      const reportCard = {
        id: "-1",
        title: bookmarkQuery.name,
        description: "",
        content_id: -1,
        bookmark: {
          type: bookmarkQuery.type,
          original_type: bookmarkQuery.type,
          name: bookmarkQuery.name,
          description: "",
          // From AI
          params: bookmarkQuery.params,
          project_id: currentProject.id
        },
        type: ContentType.Report
      } as DashboardCard;
      let cardQueryResults;
      try {
        cardQueryResults = await queryCanonicalBookmark({
          dashboard: this.props.dashboard,
          currentProject,
          card: reportCard,
          location: this.props.location,
          globalFilters: [],
          timeFilterOverride: {},
          useQueryCache: true,
          abortSignal: null,
          queryOrigin: DashboardQueryOrigin.Standard,
          metricsResource: this.props.metrics,
          behaviorsResource: this.props.behaviors
        });
        queryApiResponseTime = getNow();
      } catch (err) {
        reporter.error(err);
        throw new SparkGenerationError(SparkGenerationErrorType.QueryApiError);
      }
      const cardResult = createCardResults({
        card: reportCard,
        results: cardQueryResults,
        queryResponseTimeMs: Math.round(queryApiResponseTime - aiResponseTime)
      });
      const options = {
        dashboardMode: AppMode.Standard,
        chartOptions: getBookmarkChartOptions(reportCard.bookmark),
        hasEditPermissions: true,
        projectId: currentProject.id,
        reportUrl: (_this$props$getReport = (_this$props3 = this.props).getReportUrl) === null || _this$props$getReport === void 0 ? void 0 : _this$props$getReport.call(_this$props3, reportCard.bookmark),
        timeFilter: "{}"
      };
      const cardParams = getDashboardCardParams(reportCard as any, cardResult as any, options as any) as CardJsxProps[`params`];
      const newCards = [...this.state.cards, {
        prompt,
        bookmarkQuery,
        aiQuery,
        reportContext,
        time: moment(),
        params: _objectSpread(_objectSpread({}, cardParams), {}, {
          bookmark: reportCard === null || reportCard === void 0 ? void 0 : reportCard.bookmark,
          chartParams: cardResult === null || cardResult === void 0 ? void 0 : cardResult.chartParams,
          globalFilters: null
        })
      }];
      this.setState({
        promptInput: "",
        cards: newCards,
        currentCardIdx: newCards.length - 1,
        usage: response.usage,
        usageLimit: response.usageLimit
      });

      // track report generated
      this.trackEvent("[AI] Spark Report Generated", {
        prompt,
        reportContext: currentCard === null || currentCard === void 0 ? void 0 : currentCard.aiQuery,
        aiQuery,
        'ai time (ms)': Math.round(aiResponseTime - generationStartTime),
        'query api time (ms)': Math.round(queryApiResponseTime - aiResponseTime),
        'total time (ms)': Math.round(queryApiResponseTime - generationStartTime)
      });
    } catch (err) {
      if (err.name === "AbortError") {
        return;
      } else if (err instanceof SparkGenerationError) {
        this.setState({
          reportGenerationError: err.errorType
        });
        if (err instanceof SparkUsageLimitHitError) {
          if (getUpsellInfo(this.getCurrentProject(), ModalTrigger.SPARK, this.props.i18n)) {
            promptInProductUpsellModal(ModalTrigger.SPARK);
          }
          this.setState({
            usage: err.usage,
            usageLimit: err.usageLimit
          });
        }
      } else {
        this.setState({
          reportGenerationError: SparkGenerationErrorType.Generic
        });
      }
      const eventName = "[AI] Spark Report Generation Error";
      reporter.error(eventName, err, {
        prompt,
        error: err.message,
        aiQuery,
        reportContext: currentCard === null || currentCard === void 0 ? void 0 : currentCard.aiQuery,
        bookmarkQuery,
        'ai time (ms)': aiResponseTime ? Math.round(aiResponseTime - generationStartTime) : null,
        'query api time (ms)': queryApiResponseTime && aiResponseTime ? Math.round(queryApiResponseTime - aiResponseTime) : null,
        'total time (ms)': queryApiResponseTime ? Math.round(queryApiResponseTime - generationStartTime) : null
      });
      this.trackEvent(eventName, {
        prompt,
        error: err.message,
        reportContext: currentCard === null || currentCard === void 0 ? void 0 : currentCard.aiQuery,
        aiQuery
      });
      trackHotjarEvent(eventName);
    } finally {
      this.setState({
        isLoading: false
      });
    }
  }
  getCurrentProject(): CurrentProject | undefined {
    var _this$props$currentPr;
    return (_this$props$currentPr = this.props.currentProject) === null || _this$props$currentPr === void 0 ? void 0 : _this$props$currentPr.get().value;
  }
  getLocalizationStore(): LocalizationStore | undefined {
    return this.props.i18n;
  }
  trackEvent(eventName, properties: Record<string, any>) {
    mixpanel.track(eventName, _objectSpread({
      dashboardId: this.props.dashboard.id,
      sparkSessionId: this.sessionId
    }, properties));
  }
  render() {
    const {
      t
    } = this.props.i18n;
    const card = this.getCard();
    const hasPromptInput = !!this.state.promptInput.trim();
    const placeholder = card ? t("spark.tip-1") : t("spark.tip-2");
    const showingCard = card && !this.state.isLoading && !this.state.reportGenerationError;
    const currentProject = this.getCurrentProject();
    const usageRemaining = this.state.usageLimit - this.state.usage;

    // todo: replace this with class name once theres no shadow dom
    const rightButtonStyle = {
      pointerEvents: "all",
      '--button-icon-size': "20px",
      '--button-padding': "2px"
    };
    return <div ref={this.el} className={styles.wrapper} data-sentry-component="SparkCard" data-sentry-source-file="index.tsx">
        <div className={styles.container} style={{
        zIndex: this.state.historyOpen ? 2 : undefined
      }}>
          {!showingCard && <div className={styles.header}>
              <SvgIconReact icon='spark' />
              <span className={styles.headerTitle}>Spark</span>
              <span className={styles.betaTagWrapper}>
                <TextHighlightReact text={t("spark.beta-feature")} size={SIZE.SMALL} theme={TEXT_HIGHLIGHT_THEME.INFORMATION} />
              </span>
            </div>}
          <div className={styles.content}>
            <Content card={card} numCards={this.state.cards.length} isLoading={this.state.isLoading} reportGenerationError={this.state.reportGenerationError} positiveFeedbackProps={this.getFeedbackButtonProps(Sentiment.Positive)} negativeFeedbackProps={this.getFeedbackButtonProps(Sentiment.Negative)} currentProjectId={currentProject === null || currentProject === void 0 ? void 0 : currentProject.id} usageLimit={this.state.usageLimit} samplePrompts={this.samplePrompts} onClickContactSupport={ev => this.onClickContactSupport(ev)} onClickClearError={() => this.onClickClearError()} onClickEdit={() => this.props.onEditReport(card.bookmarkQuery)} onClickSample={promptIdx => {
            const prompt = t(this.samplePrompts[promptIdx]);
            this.setState({
              promptInput: prompt
            });
            this.submitPrompt(prompt);
          }} data-sentry-element="Content" data-sentry-source-file="index.tsx" />
          </div>
          {showingCard && <div className={styles.contentSeparator} />}
          {this.state.reportGenerationError !== SparkGenerationErrorType.UsageLimitHit && this.state.usage !== null && usageRemaining <= 10 && <div className={styles.usageWarning}>
                <SvgIconReact icon='warning' />
                <span>{t("spark.remaining-usage", {
              count: usageRemaining
            })}</span>
              </div>}
          <div className={styles.footer}>
            <div className={styles.prompt}>
              <InputReact className={styles.promptInput} placeholder={placeholder} size={InputSize.Large} elref='promptInput' readonly={this.state.isLoading} value={this.state.promptInput} autoFocus suffixRenderer={() => this.state.isLoading ? h("mp-button", {
              style: rightButtonStyle,
              attrs: {
                icon: "stop-generating",
                size: ButtonSize.Small,
                theme: ButtonTheme.Tertiary,
                tooltip: {
                  text: t("spark.stop-generating-report")
                }
              },
              on: {
                click: () => {
                  var _this$abortController2;
                  (_this$abortController2 = this.abortController) === null || _this$abortController2 === void 0 || _this$abortController2.abort();
                  this.setState({
                    isLoading: false,
                    promptInput: ""
                  });
                }
              }
            } as any) : this.state.cards.length > 0 && h("mp-button", {
              style: rightButtonStyle,
              attrs: {
                icon: "history",
                size: ButtonSize.Small,
                theme: ButtonTheme.Tertiary,
                tooltip: {
                  text: t("spark.show-history")
                }
              },
              on: {
                click: () => {
                  this.setState({
                    historyOpen: true
                  });
                  this.trackEvent("[AI] History Opened", {
                    error: this.state.reportGenerationError,
                    '# history items': this.state.cards.length
                  });
                }
              }
            } as any)} onInput={ev => {
              this.setState({
                promptInput: ev.target.value || ""
              });
            }} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
              {this.state.historyOpen && <DropMenuReact className={styles.promptHistory} direction={Directions.Down} positionStrategy={PositioningStrategy.Absolute} onChange={(ev: VisibilityChangeEvent) => {
              this.setState({
                historyOpen: ev.detail.state === Visibility.Open
              });
            }} open forceStacking>
                  <ItemsMenuReact className={styles.promptHistoryOptions} keydownProxyEl={this.el.current} onClickItem={ev => {
                const idx = +ev.detail.id;
                const previousError = this.state.reportGenerationError;
                const prevCardIdx = this.state.currentCardIdx;
                this.setState({
                  currentCardIdx: idx,
                  historyOpen: false,
                  reportGenerationError: null
                });
                this.trackEvent("[AI] History Item Selected", {
                  error: previousError,
                  '# history items': this.state.cards.length,
                  'selected item index': idx,
                  'previous item index': prevCardIdx
                });
              }} sections={this.getHistorySections()} />
                </DropMenuReact>}
            </div>
            <ButtonReact theme={ButtonTheme.Primary} size={ButtonSize.Large} disabled={!hasPromptInput || this.state.isLoading} tooltip={{
            text: "".concat(t("spark.generate-report"), " ").concat(generateShortcutLabel("Enter", {
              forMarkdown: true
            }))
          }} icon={this.state.isLoading ? "spinner" : "spark"} onClick={() => this.submitPrompt()} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
              {this.state.isLoading ? t("spark.generating") : t("spark.generate")}
            </ButtonReact>
            {card && <ButtonReact theme={ButtonTheme.Secondary} size={ButtonSize.Large} disabled={this.state.isLoading} tooltip={{
            text: "".concat(t("common.save"), " ").concat(generateShortcutLabel("Enter", {
              forMarkdown: true,
              modifiers: [getCommandKey()]
            }))
          }} icon='save' onClick={() => this.saveCard()}>
                {t("common.save")}
              </ButtonReact>}
          </div>
          <div className={styles.close}>
            <ButtonReact theme={ButtonTheme.Tertiary} size={ButtonSize.Medium} icon='x' tooltip={{
            text: "".concat(t("common.close"), " ").concat(generateShortcutLabel("Esc", {
              forMarkdown: true
            }))
          }} onClick={() => this.close()} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx" />
          </div>
        </div>
      </div>;
  }
}
function Error(props: {
  currentProjectId: number;
  reportGenerationError: SparkGenerationErrorType;
  usageLimit: number | null;
  numCards: number;
  onClickContactSupport: (ev: React.MouseEvent) => void;
  onClickClearError: () => void;
}) {
  const {
    t
  } = useTranslation();
  const goBackButton = <div className={styles.errorGoBack}>
      <ButtonReact theme={ButtonTheme.Secondary} size={ButtonSize.Medium} {...props.numCards > 0 ? {
      icon: "history"
    } : {}} onClick={props.onClickClearError}>
        {props.numCards > 0 ? t("spark.go-back-prompt") : t("spark.go-back")}
      </ButtonReact>
    </div>;
  if (props.reportGenerationError === SparkGenerationErrorType.EmptyMetrics) {
    const lexiconUrl = getReportUrl("lexicon#transformations", {
      projectId: props.currentProjectId,
      workspaceId: getWorkspaceId()
    });
    return <>
        <SvgIconReact className={styles.errorIcon} icon='search' />
        <span>
          <Trans t={t} i18nKey='spark.report-error-noselections' components={{
          'lexicon-link': <a href={lexiconUrl} />
        }} />
        </span>
        {props.numCards > 0 && goBackButton}
      </>;
  } else if (props.reportGenerationError === SparkGenerationErrorType.UsageLimitHit) {
    return <>
        <SvgIconReact className={styles.errorIcon} icon='warning' />
        <div>{t("spark.report-error-limit", {
          count: props.usageLimit
        })}</div>
        {props.numCards > 0 && goBackButton}
      </>;
  }
  return <>
      <SvgIconReact className={styles.errorIcon} icon='warning' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
      <span>
        <Trans t={t} i18nKey='spark.report-error-generic' components={{
        'support-link': <a href='#' onClick={ev => props.onClickContactSupport(ev)} />
      }} data-sentry-element="Trans" data-sentry-source-file="index.tsx" />
      </span>
      {goBackButton}
    </>;
}
function Content(props: {
  currentProjectId: number;
  isLoading: boolean;
  reportGenerationError: SparkGenerationErrorType;
  card: AICard;
  numCards: number;
  positiveFeedbackProps: ButtonJsxProps;
  negativeFeedbackProps: ButtonJsxProps;
  usageLimit: number | null;
  samplePrompts: TranslationKey[];
  onClickEdit: () => void;
  onClickSample: (promptIdx: number) => void;
  onClickContactSupport: (ev: React.MouseEvent) => void;
  onClickClearError: () => void;
}) {
  const {
    t
  } = useTranslation();
  if (props.isLoading) {
    return <div className={styles.spinnerContainer} elref='loader'>
        <SpinnerReact size={SpinnerSize.Medium} theme={SpinnerTheme.Fancy} />
      </div>;
  }
  if (props.reportGenerationError) {
    return <div className={styles.errorMessage}>
        <Error currentProjectId={props.currentProjectId} onClickContactSupport={props.onClickContactSupport} reportGenerationError={props.reportGenerationError} usageLimit={props.usageLimit} numCards={props.numCards} onClickClearError={props.onClickClearError} />
      </div>;
  }
  if (props.card) {
    return <CardReact {...props.card.params} renderTitleRightArea={() => renderCardTitleActions(styles.feedbackContainer, props.positiveFeedbackProps, props.negativeFeedbackProps)} onEditreport={ev => {
      const {
        clickEvent
      } = ev.detail;
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      props.onClickEdit();
    }} />;
  }
  return <div className={styles.examples} data-sentry-component="Content" data-sentry-source-file="index.tsx">
      {props.samplePrompts.map((promptKey, idx) => <div key={idx} className={styles.example} onClick={() => props.onClickSample(idx)}>
          <span className={styles.examplePrompt}>{t(promptKey)}</span>
        </div>)}
    </div>;
}
export const SparkCardReact = (props: SparkCardProps) => {
  const currentProject = useCurrentProject();
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const i18n = useTranslation();
  const globalSidenavController = useGlobalSidenavController();
  return <SparkCard currentProject={currentProject} metrics={metrics} behaviors={behaviors} i18n={i18n} globalSidenavController={globalSidenavController} {...props} data-sentry-element="SparkCard" data-sentry-component="SparkCardReact" data-sentry-source-file="index.tsx" />;
};
export const SparkCardEl = react2panel("mp-spark-card", SparkCardReact);